import React, { useEffect, useRef, useState } from "react";
import { uploadToCloudinary } from "@/lib/cloudinary";
import { Loader2, Video as VideoIcon, RotateCcw, CheckCircle2 } from "lucide-react";

/**
 * Live-only video recorder for identity verification.
 *
 * Key rules:
 *   - Uses getUserMedia + MediaRecorder.
 *   - NO file upload input: user MUST record in real time.
 *   - Records to a Blob in-memory, converts to File, uploads to Cloudinary.
 *   - Max duration controlled via maxSeconds (default 12s) so we don't blow up
 *     Cloudinary's 100 MB limit on mobile.
 */
export default function LiveVideoRecorder({
  onUploaded,
  folder = "verifications/",
  maxSeconds = 25,
  minSeconds = 8,
  promptText = "Lee EN VOZ ALTA mirando a cámara: \"Soy <tu nombre>, hoy <fecha>, confirmo que soy mayor de 18 años, que publico mi perfil en PK2 Scarlett por mi propia voluntad, y que NADIE me obliga ni recibe pago por mí.\"",
  testid = "live-video",
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle | ready | recording | preview | uploading | done | error
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState("");
  const [blob, setBlob] = useState(null);

  const startCamera = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 960 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});
      }
      setPhase("ready");
    } catch (e) {
      setError("No se pudo acceder a la cámara. Permite el acceso e intenta de nuevo.");
      setPhase("error");
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => {
    stopStream();
    if (timerRef.current) clearInterval(timerRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, []); // eslint-disable-line

  const pickMimeType = () => {
    const candidates = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    for (const m of candidates) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m;
    }
    return "";
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    setSeconds(0);
    const mime = pickMimeType();
    const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
    rec.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    rec.onstop = () => {
      const type = rec.mimeType || mime || "video/webm";
      const recordedBlob = new Blob(chunksRef.current, { type });
      setBlob(recordedBlob);
      const url = URL.createObjectURL(recordedBlob);
      setPreviewUrl(url);
      stopStream();
      setPhase("preview");
    };
    rec.start();
    recorderRef.current = rec;
    setPhase("recording");
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        const next = s + 1;
        if (next >= maxSeconds) stopRecording();
        return next;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const retry = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setBlob(null);
    setSeconds(0);
    setError("");
    startCamera();
  };

  const uploadRecording = async () => {
    if (!blob) return;
    setPhase("uploading");
    setError("");
    try {
      // MediaRecorder default webm — Cloudinary accepts it as "video".
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      const file = new File([blob], `liveness-${Date.now()}.${ext}`, { type: blob.type });
      const { secure_url } = await uploadToCloudinary(file, folder, "video");
      setPhase("done");
      onUploaded && onUploaded(secure_url);
    } catch (e) {
      setError(e.message || "Error al subir el video.");
      setPhase("preview");
    }
  };

  return (
    <div
      className="p-5"
      style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }}
      data-testid={testid}
    >
      <div
        className="mb-3 p-3 text-xs"
        style={{
          color: "var(--text-primary)",
          lineHeight: 1.7,
          background: "rgba(212,175,55,0.08)",
          border: "1px solid var(--vip-gold)",
        }}
        data-testid={`${testid}-script`}
      >
        <div className="flex items-center gap-2 mb-1.5" style={{ color: "var(--vip-gold)" }}>
          <VideoIcon size={14} /> Lee en voz alta mirando a cámara:
        </div>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem", color: "#fff", fontStyle: "italic" }}>
          “Soy <strong>[nombre]</strong>, hoy <strong>[fecha]</strong>, confirmo que soy mayor de 18 años,
          que publico mi perfil en <strong style={{ color: "var(--vip-gold)" }}>PK2 Scarlett</strong> por mi propia voluntad,
          y que <strong style={{ color: "var(--brand-primary)" }}>NADIE me obliga ni recibe pago por mí</strong>.”
        </div>
      </div>

      <div
        className="relative w-full mb-3 overflow-hidden"
        style={{ aspectRatio: "3/4", background: "#000" }}
      >
        {phase === "preview" && previewUrl ? (
          <video
            src={previewUrl}
            controls
            playsInline
            className="w-full h-full object-cover"
            data-testid={`${testid}-preview`}
          />
        ) : (
          <video
            ref={videoRef}
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: "scaleX(-1)" }}
            data-testid={`${testid}-live`}
          />
        )}

        {phase === "recording" && (
          <div
            className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1"
            style={{ background: "rgba(200,16,46,0.9)", color: "#fff", fontSize: "12px", fontWeight: 600 }}
          >
            <span
              style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff", display: "inline-block" }}
              className="animate-pulse"
            />
            REC · {seconds}s / {maxSeconds}s
          </div>
        )}
      </div>

      {error && <p className="text-xs mb-3" style={{ color: "#ef6d6d" }} data-testid={`${testid}-error`}>{error}</p>}

      {phase === "idle" && (
        <button type="button" className="btn-primary w-full" onClick={startCamera} data-testid={`${testid}-enable-camera`}>
          <VideoIcon size={14} className="inline mr-2" /> Activar cámara
        </button>
      )}

      {phase === "ready" && (
        <button type="button" className="btn-primary w-full" onClick={startRecording} data-testid={`${testid}-start-rec`}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fff", display: "inline-block", marginRight: 8 }} />
          Empezar a grabar
        </button>
      )}

      {phase === "recording" && (
        <button
          type="button"
          className="btn-primary w-full"
          style={{ background: "#ef6d6d" }}
          onClick={stopRecording}
          disabled={seconds < minSeconds}
          data-testid={`${testid}-stop-rec`}
        >
          {seconds < minSeconds ? `Mantén grabando ${minSeconds - seconds}s…` : "Detener grabación"}
        </button>
      )}

      {phase === "preview" && (
        <div className="flex gap-2">
          <button type="button" className="btn-ghost flex-1" onClick={retry} data-testid={`${testid}-retry`}>
            <RotateCcw size={14} className="inline mr-1" /> Grabar de nuevo
          </button>
          <button type="button" className="btn-primary flex-1" onClick={uploadRecording} data-testid={`${testid}-upload`}>
            Usar este video
          </button>
        </div>
      )}

      {phase === "uploading" && (
        <div className="flex items-center justify-center gap-3 py-3" data-testid={`${testid}-uploading`}>
          <Loader2 size={18} className="animate-spin" style={{ color: "var(--brand-primary)" }} />
          <span className="text-sm" style={{ color: "var(--text-secondary)" }}>Subiendo video…</span>
        </div>
      )}

      {phase === "done" && (
        <div className="flex items-center gap-3 p-3" style={{ background: "rgba(76,175,80,0.1)", border: "1px solid #4caf50" }} data-testid={`${testid}-done`}>
          <CheckCircle2 size={18} color="#4caf50" />
          <span className="text-sm" style={{ color: "var(--text-primary)" }}>Video listo. Continúa con el siguiente paso.</span>
        </div>
      )}

      <p className="text-[10px] mt-3 leading-relaxed" style={{ color: "var(--text-muted)" }}>
        Por seguridad, el video debe grabarse <strong style={{ color: "var(--brand-primary)" }}>en tiempo real</strong>
        &nbsp;desde tu cámara. No se permiten archivos subidos desde galería.
      </p>
    </div>
  );
}
